import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { FiShield } from 'react-icons/fi';
import { POLICY_SUM_INSURED_FIELDS } from './PolicySumInsuredFields';
import './PolicyInfoCard.css';

const EMPTY = '—';

/**
 * Affiche une date lisible (fr-FR) : 14/07/2026.
 */
export const formatPolicyDate = (value) => {
    if (value == null || value === '' || value === 'N/A' || value === '—') return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    const raw = String(value).trim();
    const slash = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slash) {
        const [, d, m, y] = slash;
        return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const [, y, m, d] = iso;
        return `${d}/${m}/${y}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }
    return raw;
};

const normalizeDateKey = (value) => formatPolicyDate(value) || '';

/** Corrige le mojibake UTF-8 courant (ex. ã« → ë). */
const fixTextEncoding = (value) => {
    if (value == null || typeof value !== 'string') return value;
    if (!/[ÃÂâ]/.test(value)) return value;
    try {
        const bytes = Uint8Array.from(
            Array.from(value, (ch) => ch.charCodeAt(0) & 0xff)
        );
        return new TextDecoder('utf-8').decode(bytes);
    } catch {
        return value;
    }
};

const displayValue = (value) => {
    if (value == null || value === '' || value === 'N/A') return EMPTY;
    const fixed = fixTextEncoding(String(value));
    return fixed === '' ? EMPTY : fixed;
};

const PolicyField = ({ label, children, highlight = false, fullWidth = false }) => (
    <div
        className={[
            'policy-info-card__field',
            highlight ? 'policy-info-card__field--updated' : '',
            fullWidth ? 'policy-info-card__field--full' : '',
        ].filter(Boolean).join(' ')}
    >
        <span className="policy-info-card__label">{label}</span>
        <div className="policy-info-card__value">{children ?? EMPTY}</div>
    </div>
);

/**
 * Section « Information police » — CSS Grid 2 colonnes, labels/valeurs alignés.
 */
const PolicyInfoCard = ({
    companyName,
    policyCode,
    reservationNumber,
    stayStartDate,
    stayEndDate,
    newStayStartDate,
    newStayEndDate,
    effectiveStartDate,
    effectiveEndDate,
    newEffectiveStartDate,
    newEffectiveEndDate,
    referenceName,
    address,
    latitude,
    longitude,
    dailySumInsuredMaintain,
    dailySumInsuredCancellation,
    totalSumInsuredMaintain,
    totalSumInsuredCancellation,
    className = '',
    title = 'Information police',
}) => {
    const sumInsuredValues = {
        daily_sum_insured_maintain: dailySumInsuredMaintain,
        daily_sum_insured_cancellation: dailySumInsuredCancellation,
        total_sum_insured_maintain: totalSumInsuredMaintain,
        total_sum_insured_cancellation: totalSumInsuredCancellation,
    };
    const startFormatted = formatPolicyDate(stayStartDate);
    const endFormatted = formatPolicyDate(stayEndDate);
    const newStartFormatted = formatPolicyDate(newStayStartDate);
    const newEndFormatted = formatPolicyDate(newStayEndDate);
    const effectiveStartFormatted = formatPolicyDate(effectiveStartDate);
    const effectiveEndFormatted = formatPolicyDate(effectiveEndDate);
    const newEffectiveStartFormatted = formatPolicyDate(newEffectiveStartDate);
    const newEffectiveEndFormatted = formatPolicyDate(newEffectiveEndDate);

    const hasNewDates = Boolean(newStartFormatted || newEndFormatted);
    const datesUpdated =
        hasNewDates
        && (
            (newStartFormatted && newStartFormatted !== normalizeDateKey(stayStartDate))
            || (newEndFormatted && newEndFormatted !== normalizeDateKey(stayEndDate))
        );

    const hasNewEffectiveDates = Boolean(
        newEffectiveStartFormatted || newEffectiveEndFormatted
    );
    const effectiveDatesUpdated =
        hasNewEffectiveDates
        && (
            (newEffectiveStartFormatted
                && newEffectiveStartFormatted !== normalizeDateKey(effectiveStartDate))
            || (newEffectiveEndFormatted
                && newEffectiveEndFormatted !== normalizeDateKey(effectiveEndDate))
        );

    const stayDatesDisplay = (startFormatted || endFormatted)
        ? [startFormatted, endFormatted].filter(Boolean).join(' → ')
        : EMPTY;

    const newDatesDisplay = hasNewDates
        ? [newStartFormatted, newEndFormatted].filter(Boolean).join(' → ')
        : EMPTY;

    const effectiveDatesDisplay = (effectiveStartFormatted || effectiveEndFormatted)
        ? [effectiveStartFormatted, effectiveEndFormatted].filter(Boolean).join(' → ')
        : EMPTY;

    const newEffectiveDatesDisplay = hasNewEffectiveDates
        ? [newEffectiveStartFormatted, newEffectiveEndFormatted].filter(Boolean).join(' → ')
        : EMPTY;

    return (
        <Card className={`policy-info-card mb-3 ${className}`.trim()}>
            <Card.Header className="policy-info-card__header">
                <FiShield className="policy-info-card__header-icon" aria-hidden />
                <span className="policy-info-card__title">{title}</span>
            </Card.Header>
            <Card.Body className="policy-info-card__body">
                <div className="policy-info-card__grid">
                    <PolicyField label="Nom de l'entreprise">
                        {displayValue(companyName)}
                    </PolicyField>
                    <PolicyField label="Code police">
                        <span className="policy-info-card__mono">{displayValue(policyCode)}</span>
                    </PolicyField>

                    <PolicyField label="Numéro de réservation">
                        {displayValue(reservationNumber)}
                    </PolicyField>
                    <PolicyField label="Nom de la référence">
                        {displayValue(referenceName)}
                    </PolicyField>

                    <PolicyField label="Adresse" fullWidth>
                        {displayValue(address)}
                    </PolicyField>

                    <PolicyField label="Latitude">
                        <span className="policy-info-card__mono">{displayValue(latitude)}</span>
                    </PolicyField>
                    <PolicyField label="Longitude">
                        <span className="policy-info-card__mono">{displayValue(longitude)}</span>
                    </PolicyField>

                    <PolicyField label="Dates du séjour">
                        {stayDatesDisplay}
                    </PolicyField>
                    <PolicyField label="Nouvelles dates" highlight={datesUpdated}>
                        <span className="policy-info-card__new-dates">
                            <span>{newDatesDisplay}</span>
                            {datesUpdated && (
                                <Badge
                                    bg="primary"
                                    pill
                                    className="policy-info-card__updated-badge"
                                >
                                    Mis à jour
                                </Badge>
                            )}
                        </span>
                    </PolicyField>

                    <PolicyField label="Dates effectives">
                        {effectiveDatesDisplay}
                    </PolicyField>
                    <PolicyField
                        label="Nouvelles dates effectives"
                        highlight={effectiveDatesUpdated}
                    >
                        <span className="policy-info-card__new-dates">
                            <span>{newEffectiveDatesDisplay}</span>
                            {effectiveDatesUpdated && (
                                <Badge
                                    bg="primary"
                                    pill
                                    className="policy-info-card__updated-badge"
                                >
                                    Mis à jour
                                </Badge>
                            )}
                        </span>
                    </PolicyField>

                    {POLICY_SUM_INSURED_FIELDS.map(({ key, label }) => (
                        <PolicyField key={key} label={label}>
                            {displayValue(sumInsuredValues[key])}
                        </PolicyField>
                    ))}
                </div>
            </Card.Body>
        </Card>
    );
};

export default PolicyInfoCard;
